import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const EditorialStandards = () => {
  const title = 'Editorial Standards & Corrections | TradeRadar';
  const description =
    'How TradeRadar sources trade intelligence, handles corrections, separates opinion from data, and stays independent of advertisers and commercial partners.';

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path="/editorial-standards"
        keywords="TradeRadar editorial policy, corrections, trade data independence"
      />

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">Editorial standards</h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          Effective March 2026 · Maintained by TradeRadar Editorial
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Mission and independence</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          TradeRadar publishes structured trade-risk intelligence for education and research. Editorial
          choices—what to explain, how to frame uncertainty, and when to delay publication—are made without
          direction from advertisers. Sponsored or affiliate placements, if ever introduced, will be
          labelled clearly and will not influence scoring methodology or event classification.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Sourcing hierarchy</h2>
        <ol className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-decimal list-inside">
          <li>Primary legal and official texts (gazettes, regulator notices, treaty depositories).</li>
          <li>Established news organisations and multilateral institutions.</li>
          <li>Industry reporting where cross-verifiable against filings or operator statements.</li>
          <li>Unverified social sources—used only with explicit caveats or not used at all.</li>
        </ol>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Automation and human review</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Machine extraction prioritises recall so analysts can find leads; editors spot-check high-impact
          paths and respond to user-reported errors. We do not claim 100% precision on every extracted
          field. Methodological detail appears in{' '}
          <Link to="/insights/methodology" className="text-primary-600 dark:text-primary hover:underline">
            the GTI methodology article
          </Link>
          .
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Corrections</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Factual errors in long-form insights or in static copy on hub pages are corrected promptly and
          noted with an updated timestamp at the top of the article where practical. Structured database
          fields are corrected at the row level; large backfills may lag single-issue fixes.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Report issues to{' '}
          <a href="mailto:contact@traderadarglobal.com" className="text-primary-600 dark:text-primary hover:underline">
            contact@traderadarglobal.com
          </a>{' '}
          or use the{' '}
          <Link to="/contact" className="text-primary-600 dark:text-primary hover:underline">
            contact form
          </Link>
          . Include the URL, a short description, and a link to a primary source if available.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Disclosures</h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          TradeRadar does not execute trades or provide personalised investment, legal, or tax advice.
          Scenario tools illustrate mechanisms; they are not forecasts. For privacy and advertising
          technologies, see the{' '}
          <Link to="/privacy-policy" className="text-primary-600 dark:text-primary hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">Related</h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2">
          <li>
            <Link to="/about" className="text-primary-600 dark:text-primary hover:underline">
              About TradeRadar
            </Link>
          </li>
          <li>
            <Link to="/insights" className="text-primary-600 dark:text-primary hover:underline">
              Insights hub
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
};

export default EditorialStandards;
